/**
 * Mobile (below `lg`) view of quick entry, rendered by `../../QuickEntryV2.tsx`: a stacked panel
 * with a "Quick Entry" title, item search, quantity + quantity UOM (`QuantityUOMV2`), price UOM
 * and a full-width Add Below button, plus `Shared/CartNotifications/NotificationPopup.tsx` →
 * `MobileTopNotificationsContainer`, which shows `add-to-cart` notifications. No keyboard
 * navigation of results (see `QuickEntryDesktopV2.tsx` for that).
 *
 * Differences from the desktop view: Add Below is enabled by `quantity` alone (the container
 * still refuses an invalid quantity), and `stateMessenger` is not passed to `QuantityUOMV2`, so
 * the `to-debounce` messages published from the price-UOM wrapper have no receiver.
 */
import { useApplication } from "@ryerson/frontend.application";
import { ContentSection, Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import React, { useState } from "react";
import { QuickEntryContent, QuickEntryText } from "../QuickEntryContent";
import { QuickEntryProps } from "../QuickEntry.models";
import { Icon } from "@ryerson/frontend.assets";
import { MobileTopNotificationsContainer } from "@components/Shared/CartNotifications/NotificationPopup";
import { UOMValue } from "@ryerson/common.uom";
import { SearchInput, ProductList, AddBelowButton, PriceUOM } from "../SharedComponents";
import { ErrorMessage, ErrorContainer, QuickEntryContainer } from "../StyledComponents";
import { QuantityUOMV2 } from "./QuantityUOMV2";

/** Mobile quick-entry panel. All state comes from `QuickEntryProps`; see the file header. */
const QuickEntryMobileV2: React.FC<QuickEntryProps> = ({
	searchInputContainerRef,
	showDropdown,
	setShowDropdown,
	searchResult,
	onClickSelectItem,
	isError,
	quantity,
	setQuantity,
	qtyUOM,
	priceUOM,
	priceUOMDropdown,
	onClickAddBelow,
	displaySearchTerm,
	onChangeText,
	searchTerm,
	setPriceUOM,
	closeQuickEntry,
	selectedProduct,
	itemAddSuccess,
	itemAddFail,
	updateQuantityUom,
	isQuickEntrySubmitted,
	handleValidationResponse,
	hasMounted,
	stateMessenger,
	qtyUOMDropdown,
}) => {
	const { theme } = useTheme();

	const backgroundPrimary = theme.colors.primary.background;
	const backgroundSecondary = theme.colors.secondary.background;
	const headerSecondary = theme.colors.secondary.header;
	const textSecondary = theme.colors.secondary.text;
	const [tempItemNumber, setTempItemNumber] = useState<any>();
	// Publish call to pubsub
	const publishMessage = (messageString: string, data: any, topic: any) => {
		/* note the use of setTimeout is required for publishing messages*/
		setTimeout(() => stateMessenger?.publish(messageString, data, topic), 10);
	};
	const {
		localization: { language },
	} = useApplication();

	const content: QuickEntryText = QuickEntryContent[language] || QuickEntryContent.en;

	React.useEffect(() => {
		if (hasMounted) {
			const _query = document && (document as any).querySelector.bind(document);
			const elInput = _query("[data-testid=quick-entry-search-input]");
			elInput?.focus();
		}
	}, [hasMounted]);

	return (
		<>
			<MobileTopNotificationsContainer />
			<ContentSection vPadding="0" hPadding="0">
				<QuickEntryContainer
					{...{ isMobile: true }}
					direction="column"
					css={{ background: backgroundSecondary }}
				>
					<Flex css={{ paddingBottom: "20px" }} justifyContent="space-between">
						<Typography color={headerSecondary}>{content.quickEntry}</Typography>
						<Icon onClick={closeQuickEntry} icon="close" circled={true} />
					</Flex>
					<FlexItem css={{ width: "100%" }}>
						<Flex
							itemRef={searchInputContainerRef}
							direction="column"
							style={css`
								width: 100%;
							`}
						>
							<SearchInput
								inputId="quick-entry-search-input"
								displaySearchTerm={displaySearchTerm}
								handleChange={(e) => onChangeText(e)}
								handleFocus={() => setShowDropdown(true)}
							/>
							{showDropdown && (
								<ProductList
									searchResults={searchResult}
									handleClick={onClickSelectItem}
								/>
							)}
							{isError && (
								<ErrorContainer {...{ color: textSecondary }}>
									<ErrorMessage
										{...{ bgColor: backgroundPrimary, bColor: textSecondary }}
									>
										{"No Results Found".replace(
											"%searchString%",
											searchTerm.toString()
										)}
									</ErrorMessage>
								</ErrorContainer>
							)}
						</Flex>
					</FlexItem>
					<Flex alignItems="flex-end" css={{ width: "100%", paddingTop: "20px" }}>
						<Flex
							direction="column"
							style={css`
								padding-right: 10px;
								width: 100%;
							`}
						>
							<QuantityUOMV2
								id="quick-entry"
								itemData={selectedProduct || {}}
								quantityUomOptions={qtyUOMDropdown}
								size="sm"
								type="primary"
								disabled={!selectedProduct}
								dataTestId="quantity-uom-test"
								isFocused={!!selectedProduct}
								isMobile={true}
								isSubmitted={isQuickEntrySubmitted}
								quantityValue={quantity}
								uomValue={qtyUOM as UOMValue}
								handleChange={(quantityValue, uomValue) => {
									setQuantity(quantityValue);
								}}
								handleUOMChange={(uomValue) => updateQuantityUom(uomValue)}
								handleValidationResponse={handleValidationResponse}
								recalculateQuantities={false}
								forceTrigger={true}
							/>
						</Flex>
					</Flex>
					<Flex
						direction="column"
						style={css`
							padding-top: 20px;
							width: 100px;
							padding-right: 10px;
						`}
					>
						<div
							onFocus={() => publishMessage(`to-debounce`, "focus", "quick-entry")}
							onChange={(value) =>
								publishMessage(`to-debounce`, "change", "quick-entry")
							}
						>
							<PriceUOM
								isMobile={true}
								handleChange={setPriceUOM}
								isDisabled={() => {
									return !selectedProduct || priceUOMDropdown.length === 1;
								}}
								priceUOM={priceUOM}
								priceUOMDropdown={priceUOMDropdown}
							/>
						</div>
					</Flex>

					<FlexItem css={{ width: "100%" }}>
						<AddBelowButton
							isMobile={true}
							itemNumber={tempItemNumber}
							itemAddSuccess={itemAddSuccess}
							itemAddFail={itemAddFail}
							handleClick={() => {
								setTempItemNumber(selectedProduct?.partNumber);
								onClickAddBelow();
							}}
							isDisabled={!quantity}
							buttonClassName={""}
						/>
					</FlexItem>
				</QuickEntryContainer>
			</ContentSection>
		</>
	);
};

export default QuickEntryMobileV2;
